import { ReactNode, RefObject, useRef, useState } from 'react';

import { AnchorLink } from '@displate/styleguide';
import clsx from 'clsx';

import { trackGA4SelectPromotion } from '@/common/analytics/gtm/shared/trackPromotionClick';
import { imageCoreToAsset } from '@/common/components/ArtworkPicture/artworkUri';
import { StaticImage } from '@/common/constants/displateRoutes';
import { BaseMessage } from '@/common/impressions/common';
import { RouteToArtistPage } from '@/common/routes/ExternalRoutes';
import { noImageUrl } from '@/modules/Lumino/LuminoPage/constants';
import { HomepageBrandsFeedItem } from 'generatedBfw/static';

import styles from './brandTile.module.scss';

interface BrandTileProps {
  brand: HomepageBrandsFeedItem;
  className?: string;
  classNameImage?: string;
  classNameLink?: string;
  footerSlot?: ReactNode;
  loading?: 'lazy' | 'eager';
}

interface PropsWithTracking<EventType extends BaseMessage> extends BrandTileProps {
  useBrandImpression: (
    slideRef: RefObject<Element>,
    message: EventType,
  ) => { onHold: boolean; setOnHold: (isOnHold: boolean) => void };
  metadata: EventType;
}

interface PropsWithoutTracking extends BrandTileProps {
  useBrandImpression?: undefined;
  metadata?: undefined;
}

const BrandTile = <EventType extends BaseMessage>({
  brand,
  className,
  classNameImage,
  classNameLink,
  metadata,
  useBrandImpression,
  footerSlot,
  loading = 'lazy',
}: PropsWithTracking<EventType> | PropsWithoutTracking) => {
  const ref = useRef(null);
  const [showFallback, setShowFallback] = useState(false);

  const { setOnHold } = useBrandImpression?.(ref, metadata) || {};

  const handleError = (): void => {
    setShowFallback(true);
    setOnHold?.(true);
  };

  return (
    <div className={clsx(styles.brandTile, className)} data-testid="brand-tile" ref={ref}>
      <div className={styles.imageWrapper}>
        {showFallback ? (
          <img
            alt="no-image"
            className={clsx(styles.image, styles.fallbackImage)}
            height={384}
            loading={loading}
            src={StaticImage(noImageUrl)}
            width={582}
          />
        ) : (
          <img
            alt={`${brand.name} logo`}
            className={clsx(styles.image, classNameImage)}
            draggable={false}
            height={384}
            loading={loading}
            onError={handleError}
            src={imageCoreToAsset(brand.coverCoreUrl, 'brand/layout')}
            width={582}
          />
        )}
      </div>
      <div className={styles.content}>
        <div className={styles.info}>
          <AnchorLink
            href={RouteToArtistPage({ licensed: true, nick: brand.slug }).route}
            onClick={() => trackGA4SelectPromotion(metadata)}
          >
            <h3 className={clsx(styles.link, classNameLink)}>{brand.name}</h3>
          </AnchorLink>
          <p className={styles.artworks}>{brand.artworksCount} artworks</p>
        </div>
        {footerSlot ? <div className={styles.footer}>{footerSlot}</div> : null}
      </div>
    </div>
  );
};

export default BrandTile;
